import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { ApiClientError } from '@/lib/api/client'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import { isCampaignTotpStepUpError } from '@/lib/campaign-totp-step-up'
import { formatRouletteUserError } from '@/lib/formatRouletteUserError'
import {
  createOrUpdateRouletteConfig,
  getRouletteConfig,
  type RouletteConfig,
  type RouletteConfigInput,
} from '@/lib/roulette'
import type { RoletaSlotState } from '@/pages/CampanhasPage/roletaSlotTypes'
import { buildPublicDisplayFromDraft } from '@/pages/CampanhasPage/roletaPreviewMapping'
import { RoletaConfigForm } from './RoletaConfigForm'
import { RoletaPrizesEditor } from './RoletaPrizesEditor'
import { RoletaPreview } from './RoletaPreview'
import { RoletaDistributedPrizesTable } from './RoletaDistributedPrizesTable'
import styles from './CampanhasPage.module.css'
import type { CampaignEditorSaveTask } from './editor/campaignEditorSaveTask'
import type { RoletaSaveTask } from './roletaSaveTask'

const DEFAULT_POSITIONS = 8

function clampPositions(n: number): number {
  if (!Number.isFinite(n)) return DEFAULT_POSITIONS
  return Math.min(16, Math.max(2, Math.round(n)))
}

export interface RoletaPanelProps {
  campaignId: number
  token: string | null
  onSaved?: () => void
  onSaveStateChange?: (state: CampaignEditorSaveTask | null) => void
  savingCampaign?: boolean
}

export function RoletaPanel({
  campaignId,
  token,
  onSaved,
  onSaveStateChange,
  savingCampaign = false,
}: RoletaPanelProps) {
  const { runWithStepUp } = useCampaignTotpStepUp()
  const [config, setConfig] = useState<RouletteConfig | null>(null)
  const [isLoadingConfig, setIsLoadingConfig] = useState(true)
  const [configSaveError, setConfigSaveError] = useState<string | null>(null)
  const [configLoadError, setConfigLoadError] = useState<string | null>(null)
  const [isSavingConfig, setIsSavingConfig] = useState(false)
  const [configTask, setConfigTask] = useState<RoletaSaveTask | null>(null)
  const [prizesTask, setPrizesTask] = useState<RoletaSaveTask | null>(null)
  const [isSavingAll, setIsSavingAll] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const savingRef = useRef(false)
  const [livePositions, setLivePositions] = useState<number>(DEFAULT_POSITIONS)
  const [draftSlots, setDraftSlots] = useState<RoletaSlotState[] | null>(null)

  useEffect(() => {
    setDraftSlots(null)
    setConfig(null)
    setConfigLoadError(null)
    setConfigSaveError(null)
    setLivePositions(DEFAULT_POSITIONS)
  }, [campaignId])

  useEffect(() => {
    if (!token) {
      setIsLoadingConfig(false)
      setConfig(null)
      return
    }

    let cancelled = false
    setIsLoadingConfig(true)
    setConfigLoadError(null)

    getRouletteConfig(campaignId, token)
      .then((fetched) => {
        if (cancelled) return
        if (fetched) {
          setConfig(fetched)
          setLivePositions(clampPositions(fetched.positions))
        } else {
          setConfig(null)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setConfigLoadError('Não foi possível carregar a configuração.')
          setConfig(null)
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingConfig(false)
      })

    return () => {
      cancelled = true
    }
  }, [campaignId, token])

  const handleSaveConfig = useCallback(
    async (input: RouletteConfigInput) => {
      if (!token) throw new Error('Sessão indisponível. Entre novamente para salvar.')
      setConfigSaveError(null)
      setIsSavingConfig(true)
      try {
        const saved = await runWithStepUp(() =>
          createOrUpdateRouletteConfig(campaignId, input, token)
        )
        setConfig(saved)
        setLivePositions(clampPositions(saved.positions))
      } catch (err) {
        const message = isCampaignTotpStepUpError(err)
          ? err.message
          : err instanceof ApiClientError && err.status === 403
            ? 'Selecione uma instituição no menu para configurar a roleta.'
            : formatRouletteUserError(err)
        setConfigSaveError(message)
        throw err
      } finally {
        setIsSavingConfig(false)
      }
    },
    [campaignId, runWithStepUp, token]
  )

  const hasChanges = Boolean(configTask?.hasChanges || prizesTask?.hasChanges)
  const saveAll = useCallback(async () => {
    if (
      savingRef.current ||
      !token ||
      isLoadingConfig ||
      configLoadError ||
      !configTask ||
      (config && !prizesTask)
    )
      return false
    const tasks = [configTask, prizesTask].filter((task): task is RoletaSaveTask =>
      Boolean(task?.hasChanges)
    )
    // Valide tudo antes da primeira escrita. A API exige salvar posições antes dos prêmios.
    if (tasks.some((task) => !task.validate())) return false
    savingRef.current = true
    setIsSavingAll(true)
    setSaveSuccess(false)
    try {
      for (const task of tasks) {
        if (!(await task.save())) return false
      }
      if (tasks.length > 0) {
        setSaveSuccess(true)
        onSaved?.()
      }
      return true
    } finally {
      savingRef.current = false
      setIsSavingAll(false)
    }
  }, [token, isLoadingConfig, configLoadError, config, configTask, prizesTask, onSaved])

  useEffect(() => {
    onSaveStateChange?.({ hasChanges, saving: isSavingAll, save: saveAll })
  }, [hasChanges, isSavingAll, saveAll, onSaveStateChange])
  useEffect(() => () => onSaveStateChange?.(null), [onSaveStateChange])

  const saveDisabled =
    isSavingAll ||
    savingCampaign ||
    !token ||
    Boolean(configLoadError) ||
    !configTask ||
    Boolean(config && !prizesTask)

  const effectivePositions = clampPositions(livePositions)
  const configPositionsMismatch =
    config != null && effectivePositions !== config.positions

  const draftDisplay = useMemo(
    () =>
      draftSlots !== null
        ? buildPublicDisplayFromDraft(effectivePositions, draftSlots)
        : null,
    [draftSlots, effectivePositions]
  )

  if (isLoadingConfig) {
    return (
      <div className={styles.contentStack}>
        <section className={styles.panelCard}>
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinner} />
            <span>Carregando configuração da roleta...</span>
          </div>
        </section>
      </div>
    )
  }

  return (
    <div className={styles.contentStack}>
      {configLoadError && (
        <div className={styles.errorBanner} role="alert">
          {configLoadError}
        </div>
      )}
      <div
        className={hasChanges ? styles.warningBanner : styles.successBanner}
        role="status"
      >
        {hasChanges
          ? 'Há alterações na roleta para salvar. Use “Salvar roleta” em qualquer painel ou o botão verde para salvar configurações e prêmios juntos.'
          : saveSuccess
            ? 'Todas as alterações da roleta foram salvas.'
            : 'Configure as regras de giros e depois os prêmios. As probabilidades dos prêmios devem somar 100%.'}
      </div>
      <RoletaConfigForm
        key={`config-${campaignId}`}
        config={config}
        onSave={handleSaveConfig}
        onSaveAll={saveAll}
        onSaveStateChange={setConfigTask}
        isSaving={isSavingConfig || isSavingAll || savingCampaign}
        saveDisabled={saveDisabled}
        errorMessage={configSaveError}
        onPositionsChange={setLivePositions}
      />
      {configPositionsMismatch && (
        <div className={styles.errorBanner} role="status">
          Quantidade de posições alterada. Revise os prêmios e complete 100% de
          probabilidade. Ao salvar, as posições e os prêmios serão atualizados juntos.
        </div>
      )}
      <RoletaPrizesEditor
        key={`prizes-${campaignId}`}
        campaignId={campaignId}
        positions={effectivePositions}
        savedPositions={config?.positions}
        token={token}
        configExists={config != null}
        onDraftChange={setDraftSlots}
        onSaveAll={saveAll}
        onSaveStateChange={setPrizesTask}
        savingAll={isSavingAll || savingCampaign}
        saveDisabled={saveDisabled}
      />
      {config && draftDisplay && (
        <RoletaPreview
          campaignId={campaignId}
          positions={effectivePositions}
          token={token}
          draftDisplay={draftDisplay}
        />
      )}
      <RoletaDistributedPrizesTable
        key={campaignId}
        campaignId={campaignId}
        token={token}
      />
    </div>
  )
}
